import { c as _c } from "react/compiler-runtime";
import type { BigIntish, Fetchable, MarketId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { type ApiInputToken, type MarketRewards, parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { bigIntComparator, isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import type { MarketWarningFields } from '@/types/fields';
import { getMarketWithPoints } from '@/components/charts/helpers/getPoints';
import { useWeb3Context } from '@/contexts/Web3Context';
import { aggregateMarketWarnings } from '@/helpers/aggregateMarketWarnings';
import { getParsedValue } from '@/helpers/formatters';
import { getMarketRewards } from '@/helpers/rewards';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
export interface MarketForRow {
  marketId: MarketId;
  loanAsset: ApiInputToken;
  collateralAsset: ApiInputToken | null;
  lltv: BigIntish;
  reallocatableLiquidityAssets: BigIntish | null;
  warnings: MarketWarningFields[];
  state: {
    avgBorrowApy: number | null;
    avgNetBorrowApy: number | null;
    liquidityAssets: BigIntish | null;
    liquidityAssetsUsd: BigIntish | null;
    rewards?: MarketRewards[] | null;
  } | null;
}
export interface UseSelectMarketRowsProps {
  markets: Fetchable<MarketForRow[]>;
  selectedMarketId: Fetchable<MarketId>;
}
const useSelectMarketRows = t0 => {
  const $ = _c(10);
  const {
    markets,
    selectedMarketId
  } = t0;
  const {
    showPoints
  } = useFeatureFlags();
  const {
    targetChain
  } = useWeb3Context();
  let t1;
  bb0: {
    if (!markets) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== markets || $[2] !== selectedMarketId || $[3] !== showPoints || $[4] !== targetChain.id) {
      let t3;
      if ($[6] !== selectedMarketId || $[7] !== showPoints || $[8] !== targetChain.id) {
        t3 = market => {
          const loanAsset = parseApiAsset(market.loanAsset);
          const collateralAsset = parseApiAsset(market.collateralAsset);
          const rewards = getMarketRewards(market, "borrow");
          const avgNetBorrowApy = getParsedValue(market.state?.avgNetBorrowApy, safeParseNumber);
          const avgBorrowApy = getParsedValue(market.state?.avgBorrowApy, safeParseNumber);
          const points = showPoints ? getMarketWithPoints(targetChain.id, market.marketId, collateralAsset?.address).filter(isDefined) : [];
          const liquidity = (toBigInt(market.state?.liquidityAssets) ?? ZERO) + (toBigInt(market.reallocatableLiquidityAssets) ?? ZERO);
          const liquidityUsd = loanAsset.toUsd(liquidity);
          const aggregatedWarnings = aggregateMarketWarnings({
            market
          });
          return {
            market: {
              ...market,
              warnings: aggregatedWarnings
            },
            loanAsset,
            collateralAsset,
            rewards,
            liquidity: {
              amount: liquidity,
              value: liquidityUsd
            },
            netApy: {
              avgNetBorrowApy,
              avgBorrowApy,
              rewards,
              points
            },
            isSelected: market.marketId === selectedMarketId
          };
        };
        $[6] = selectedMarketId;
        $[7] = showPoints;
        $[8] = targetChain.id;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      t2 = markets.map(t3).toSorted(bigIntComparator(_temp, "desc"));
      $[1] = markets;
      $[2] = selectedMarketId;
      $[3] = showPoints;
      $[4] = targetChain.id;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = t2;
  }
  return t1;
};
export default useSelectMarketRows;
function _temp(a) {
  return a.liquidity.value;
}